import type {
  ImperativeDialogCustomProps,
  ImperativeDialogEntry,
  ImperativeDialogProps,
  ImperativeDialogResult,
  ImperativeDialogVariant,
} from "./imperative-dialog.types";

type Resolver = (result: ImperativeDialogResult) => void;

let current: ImperativeDialogEntry | null = null;
let pending: Resolver | null = null;

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

function resolveIfPending(result: ImperativeDialogResult) {
  const resolve = pending;
  pending = null;
  resolve?.(result);
}

export function dismiss(): void {
  if (!current) {
    return;
  }
  current.props.onDismiss?.();
  resolveIfPending({ reason: "dismiss" });
  current = null;
  emit();
}

export function getServerSnapshot(): ImperativeDialogEntry | null {
  return null;
}

export function getSnapshot(): ImperativeDialogEntry | null {
  return current;
}

export function openCustom(
  props: ImperativeDialogCustomProps,
): Promise<ImperativeDialogResult> {
  return open("custom", props);
}

export function openError(
  props: ImperativeDialogProps,
): Promise<ImperativeDialogResult> {
  return open("error", props);
}

export function openSuccess(
  props: ImperativeDialogProps,
): Promise<ImperativeDialogResult> {
  return open("success", props);
}

/**
 * Resolves the open dialog after a primary or secondary action, awaiting the
 * action `onClick` when present. If the entry was replaced or dismissed while
 * awaiting, this becomes a no-op.
 */
export async function resolveFromUserAction(
  reason: "action" | "secondaryAction",
): Promise<void> {
  const snapshot = current;
  if (!snapshot) {
    return;
  }
  const action =
    reason === "action"
      ? snapshot.props.action
      : snapshot.props.secondaryAction;
  try {
    await action?.onClick?.();
  } finally {
    if (current === snapshot) {
      resolveIfPending({ reason });
      current = null;
      emit();
    }
  }
}

function open(
  variant: ImperativeDialogVariant,
  props: ImperativeDialogCustomProps,
): Promise<ImperativeDialogResult> {
  resolveIfPending({ reason: "dismiss" });
  current = { props, variant };
  emit();
  return new Promise((resolve) => {
    pending = resolve;
  });
}

/**
 * Clears dialog state and settles any pending promise (for test isolation).
 * Does not invoke `onDismiss`.
 */
export function resetImperativeDialogStoreForTests(): void {
  resolveIfPending({ reason: "dismiss" });
  current = null;
  emit();
}

export function subscribe(listener: () => void) {
  listeners.add(listener);
  return function unsubscribe() {
    listeners.delete(listener);
  };
}
